import {
  AbilityBuilder,
  createMongoAbility,
  MongoAbility,
  MongoQuery,
  PureAbility,
} from '@casl/ability';
import {
  useGetESSUserPermissionsQuery,
  // TODO: Commented while MR won't be delivered in prod
  useGetMRUserPermissionsQuery,
} from '@Store/api/permissionsApiSlice';
import { getMREnable } from '@Utils/getFeatureFlag';

export const ESSActions = [
  'Get',
  'Update',
  'Validate',
  'Submit',
  'Approve',
  'List',
  'History',
  'Delete',
  'Cancel',
  'Create',
] as const;

const ESSRequests = [
  'Leave',
  'Passport',
  'Resumption',
  'Salary',
  'Overseas',
  'ITRequisition',
  'Resignation',
  'Attendance',
] as const;

export const MRActions = [
  'Get',
  'Search',
  'Update',
  'Import',
  'Export',
  'Delete',
  'GetMaterials',
  'GetDocs',
  'GetSpecDoc',
  'Create',
  'Approve',
  'Cancel',
  'Submit',
  'UpdateMode',
  'Extend',
  'Compare',
  'History',
  'CreateSpecDoc',
  'DeleteSpecDoc',
  'Copy',
  'GetVendors',
  'DeleteMaterials',
  'Unlock',
  'GetVendorDetail',
  'SaveQuantity',
  'AddRemark',
] as const;

const MRRequests = [
  'PurchaseRequest',
  'TenderRequest',
  'TenderReply',
  'Material',
  'MaterialRequestItem',
] as const;

const requestTypesMap: Record<string, string> = {
  LeaveRequest: 'Leave',
  PassportRequest: 'Passport',
  ResumptionRequest: 'Resumption',
  SalaryCertificate: 'Salary',
  OverseasMission: 'Overseas',
  ITRequisition: 'ITRequisition',
  ResignationRequest: 'Resignation',
  AttendanceRequest: 'Attendance',
  PurchaseRequest: 'PurchaseRequest',
  TenderRequest: 'TenderRequest',
  TenderReply: 'TenderReply',
  MaterialRequest: 'Material',
  MaterialRequestItem: 'MaterialRequestItem',
};

type ESSACtion = (typeof ESSActions)[number];
type ESSRequest = (typeof ESSRequests)[number];

type MRACtion = (typeof MRActions)[number];
type MRRequest = (typeof MRRequests)[number];

type AppAction = ESSACtion | MRACtion;
type AppRequest = ESSRequest | MRRequest;
type AppAbility = PureAbility<[AppAction, AppRequest], MongoQuery>;

const useUserPermissions = (): MongoAbility => {
  const isMREnable = getMREnable();
  const { data: essData = [] } = useGetESSUserPermissionsQuery();

  const { data: mrData = [] } = useGetMRUserPermissionsQuery(undefined, {
    skip: !isMREnable,
  });

  // TODO: MR won't be delivered in prod
  const mrPermission = isMREnable ? mrData : [];
  const permissions = [...essData, ...mrPermission];

  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

  permissions.forEach((permission) => {
    const matchedAction =
      ESSActions.find((action) => permission.startsWith(action)) ??
      MRActions.find((action) => permission.startsWith(action));

    if (!matchedAction) return;

    const requestType = permission.slice(matchedAction.length);

    const mappedRequestType = requestTypesMap[requestType] ?? requestType;

    const isValidRequest =
      ESSRequests.includes(mappedRequestType as ESSRequest) ||
      MRRequests.includes(mappedRequestType as MRRequest);

    if (!isValidRequest) return;

    can(matchedAction as AppAction, mappedRequestType as AppRequest);
  });

  return build();
};

export default useUserPermissions;
